import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

function LibraryBulkImport({ onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const loadHistory = async () => {
    try {
      const response = await axiosInstance.get("library/import/history/");
      setHistory(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const downloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("library/import/template/", { responseType: "blob" });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "the_grange_library_import_template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Could not download the library template.");
    }
  };

  const upload = async (endpoint) => {
    if (!file) { toast.warning("Choose a CSV file first."); return; }
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    try {
      const response = await axiosInstance.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (endpoint.includes("preview")) {
        setPreview(response.data);
        setResult(null);
        toast.success("Library CSV checked successfully.");
      } else {
        setResult(response.data);
        setPreview(null);
        toast.success(`${response.data.imported_rows} library items imported.`);
        await loadHistory();
        if (onImported) await onImported();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Library import failed.");
    } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="border rounded-4 p-3 p-lg-4 bg-light mb-4">
        <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
          <div>
            <h6 className="fw-bold mb-1">Bulk catalogue import</h6>
            <p className="text-muted mb-0">Upload books, textbooks, magazines, references and digital resources from one CSV file.</p>
          </div>
          <button className="btn btn-outline-success" type="button" onClick={downloadTemplate}>
            <i className="bi bi-download me-2"></i>Download Template
          </button>
        </div>

        <div className="row g-3 mt-1 align-items-end">
          <div className="col-lg-7">
            <label className="form-label fw-semibold">Completed CSV file</label>
            <input className="form-control" type="file" accept=".csv,text/csv" onChange={(event) => { setFile(event.target.files?.[0] || null); setPreview(null); setResult(null); }} />
          </div>
          <div className="col-lg-5 d-flex flex-wrap gap-2">
            <button className="btn btn-outline-primary" type="button" disabled={loading || !file} onClick={() => upload("library/import/preview/")}>
              {loading ? "Checking..." : "Preview & Validate"}
            </button>
            <button className="btn btn-success" type="button" disabled={loading || !preview?.valid_rows} onClick={() => upload("library/import/commit/")}>
              Import Valid Items
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white d-flex flex-wrap gap-3 justify-content-between align-items-center">
            <h6 className="fw-bold mb-0">Validation Preview</h6>
            <div className="d-flex gap-2">
              <span className="badge bg-secondary">Total {preview.total_rows}</span>
              <span className="badge bg-success">Valid {preview.valid_rows}</span>
              <span className="badge bg-danger">Invalid {preview.invalid_rows}</span>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light"><tr><th>Row</th><th>Title</th><th>Author</th><th>Category</th><th>Copies</th><th>Status</th></tr></thead>
              <tbody>{preview.rows.map((row) => (
                <tr key={row.row_number}>
                  <td>{row.row_number}</td><td className="fw-semibold">{row.data.title || "-"}</td><td>{row.data.author || "-"}</td><td>{row.data.category_name || "-"}</td><td>{row.data.total_copies || 1}</td>
                  <td>{row.valid ? <span className="badge bg-success">Ready</span> : <div><span className="badge bg-danger mb-1">Fix row</span>{row.errors.map((error) => <div className="small text-danger" key={error}>{error}</div>)}</div>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className={`alert ${result.failed_rows ? "alert-warning" : "alert-success"}`}>
          <strong>Import complete:</strong> {result.imported_rows} imported, {result.failed_rows} failed out of {result.total_rows}.
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white"><h6 className="fw-bold mb-0">Recent Library Imports</h6></div>
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead className="table-light"><tr><th>File</th><th>Uploaded By</th><th>Date</th><th>Imported</th><th>Failed</th><th>Status</th></tr></thead>
            <tbody>{history.length ? history.map((row) => (
              <tr key={row.id}><td>{row.file_name}</td><td>{row.uploaded_by}</td><td>{new Date(row.created_at).toLocaleString()}</td><td>{row.imported_rows}</td><td>{row.failed_rows}</td><td><span className={`badge ${row.status === "COMPLETED" ? "bg-success" : row.status === "FAILED" ? "bg-danger" : "bg-warning text-dark"}`}>{row.status}</span></td></tr>
            )) : <tr><td colSpan="6" className="text-center text-muted py-4">No library imports yet.</td></tr>}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default LibraryBulkImport;
